import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  loginUser, 
  clearError, 
  clearApiMessage,
  selectAuthLoading, 
  selectAuthError,
  selectApiMessage 
} from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  LoaderCircle,
  CheckCircle,
  XCircle,
  LogIn
} from 'lucide-react';

const Login = ({ showToast }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const apiMessage = useSelector(selectApiMessage);
  
  const from = location.state?.from?.pathname || '/dashboard';

  // Styles matching Register component
  const inputStyles = {
    width: '100%',
    padding: '16px 20px 16px 50px',
    fontSize: '17px',
    borderRadius: '10px',
    border: '2px solid #e2e8f0',
    background: '#ffffff',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    color: '#1a1a2e',
    outline: 'none',
    lineHeight: '1.5',
  };

  const labelStyles = {
    display: 'block',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#1a1a2e',
    fontSize: '15px',
  };

  const inputWrapperStyles = {
    marginBottom: '20px',
    position: 'relative',
  };

  const iconWrapperStyles = {
    position: 'absolute',
    left: '18px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    pointerEvents: 'none',
    zIndex: 1,
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    dispatch(clearError());
    dispatch(clearApiMessage());
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    dispatch(clearApiMessage());

    try {
      const result = await dispatch(loginUser({ 
        email: email.trim(), 
        password: password.trim() 
      })).unwrap();
      
      if (result.message && showToast) {
        showToast(result.message, 'success');
      }
      
      if (rememberMe) {
        localStorage.setItem('remembered_email', email);
      } else {
        localStorage.removeItem('remembered_email');
      }
      
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 0);
      
    } catch (err) {
      const errorMsg = err.message || '';

      // Extract API response data
      const apiData = err.apiResponse?.data || err.data || {};
      const requiresVerification = apiData?.requires_phone_verification === true;

      // Check if error indicates phone verification is required
      if (requiresVerification) {
        const phone = apiData?.phone || '';
        if (phone) {
          navigate('/verify-otp', {
            state: {
              phone,
              autoSend: false,
              message: apiData?.message || err.apiResponse?.message || 'An OTP has been sent to your phone. Please check your messages.'
            }
          });
          return;
        }
      }

      // Fallback: if error message contains verification keywords but no phone, still show toast
      const isPhoneVerificationError =
        errorMsg.toLowerCase().includes('verify') ||
        errorMsg.toLowerCase().includes('phone') ||
        errorMsg.toLowerCase().includes('unverified');

      if (isPhoneVerificationError && showToast) {
        showToast(errorMsg, 'error');
      } else if (showToast) {
        showToast(errorMsg, 'error');
      }
    }
  };

  return (
    <AuthLayout title="Welcome Back" subtitle="Sign in to your account">
      <form onSubmit={handleSubmit} style={{ maxWidth: '520px', margin: '0 auto' }}>
        {apiMessage && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: error ? '#fee2e2' : '#d1fae5',
            color: error ? '#dc2626' : '#065f46',
            border: `1px solid ${error ? '#fecaca' : '#a7f3d0'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            {error ? <XCircle size={20} /> : <CheckCircle size={20} />}
            <span>{apiMessage}</span>
          </div>
        )}

        <div style={inputWrapperStyles}>
          <label style={labelStyles}>Email Address</label>
          <div style={{ position: 'relative' }}>
            <span style={iconWrapperStyles}><Mail size={20} /></span>
            <input
              type="email"
              style={{ ...inputStyles, borderColor: '#e2e8f0' }}
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                dispatch(clearError());
                dispatch(clearApiMessage());
              }}
              required
              disabled={loading}
              autoComplete="email"
              onFocus={(e) => e.target.style.borderColor = '#d4af37'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>
        </div>

        <div style={inputWrapperStyles}>
          <label style={labelStyles}>Password</label>
          <div style={{ position: 'relative' }}>
            <span style={iconWrapperStyles}><Lock size={20} /></span>
            <input
              type={showPassword ? 'text' : 'password'}
              style={{ ...inputStyles, paddingRight: '50px', borderColor: '#e2e8f0' }}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                dispatch(clearError());
                dispatch(clearApiMessage());
              }}
              required
              minLength={6}
              disabled={loading}
              autoComplete="current-password"
              onFocus={(e) => e.target.style.borderColor = '#d4af37'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                color: '#94a3b8',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: loading ? 0.5 : 1
              }}
            >
              {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            fontSize: '14px', 
            color: '#64748b', 
            cursor: 'pointer' 
          }}>
            <input 
              type="checkbox" 
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ 
                accentColor: '#d4af37',
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }} 
            />
            Remember me
          </label>
          <Link to="/forgot-password" style={{ 
            color: '#d4af37', 
            fontWeight: '600',
            textDecoration: 'none',
            fontSize: '14px'
          }}>
            Forgot password?
          </Link>
        </div>

        <button 
          type="submit" 
          disabled={loading}
          style={{
            width: '100%',
            padding: '18px',
            fontSize: '18px',
            fontWeight: '700',
            borderRadius: '10px',
            backgroundColor: '#d4af37',
            color: '#1a1a2e',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: loading ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}
          onMouseEnter={(e) => { if (!loading) e.target.style.backgroundColor = '#c5a032'; }}
          onMouseLeave={(e) => { if (!loading) e.target.style.backgroundColor = '#d4af37'; }}
        >
          {loading ? (
            <>
              <LoaderCircle size={22} style={{ animation: 'spin 1s linear infinite' }} />
              Signing in...
            </>
          ) : (
            <>
              <LogIn size={20} /> Sign In
            </>
          )}
        </button>

        <div style={{ 
          margin: '24px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px'
        }}>
          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e2e8f0' }} />
          <span style={{ color: '#94a3b8', fontSize: '14px' }}>OR</span>
          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #e2e8f0' }} />
        </div>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '15px', 
          color: '#64748b',
          marginBottom: '8px'
        }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ 
            color: '#d4af37', 
            fontWeight: '700',
            textDecoration: 'none',
            fontSize: '15px'
          }}>
            Create one
          </Link>
        </p>
      </form>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </AuthLayout>
  );
};

export default Login;